import { type FC, useCallback, useRef, useState } from "react";
import { Info } from "lucide-react";

import papersIcon from "@/shared/assets/icons/papers-icon.svg";
import { FileCard } from "@/shared/components/ui/file-card";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/shared/components/ui/tooltip";

export const ACCEPTED_TYPES = ".pdf,.jpg,.jpeg,.png,.heic,.heif";
export const ACCEPTED_LABEL = "PDF, PNG, JPG, or HEIC";

export const UPLOAD_ZONE_CLASSES =
  "flex h-[200px] cursor-pointer flex-col items-center justify-center rounded-lg border border-dashed bg-white transition-colors duration-75 ease-out hover:border-strong hover:bg-subtle";

export function uploadZoneClasses(isDragging: boolean): string {
  return `${UPLOAD_ZONE_CLASSES} ${isDragging ? "border-strong bg-subtle" : "border-primary"}`;
}

interface UploadZoneProps {
  label?: string;
  labelSuffix?: string;
  labelTooltip?: string;
  file: File | null;
  onFileSelect: (file: File) => void;
  onFileRemove: () => void;
  accept?: string;
  acceptLabel?: string;
  existingFileName?: string | null;
  fileCardStatus?: "idle" | "loading" | "complete" | "error";
  fileCardProgress?: number;
  fileCardPreviewUrl?: string | null;
  fileCardErrorMessage?: string;
}

export const UploadZone: FC<UploadZoneProps> = ({
  label,
  labelSuffix,
  labelTooltip,
  file,
  onFileSelect,
  onFileRemove,
  accept = ACCEPTED_TYPES,
  acceptLabel = ACCEPTED_LABEL,
  existingFileName,
  fileCardStatus,
  fileCardProgress,
  fileCardPreviewUrl,
  fileCardErrorMessage,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [existingRemoved, setExistingRemoved] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      onFileSelect(selectedFile);
    }
  };

  const handleRemoveFile = () => {
    setExistingRemoved(true);
    onFileRemove();
    if (inputRef.current) {
      inputRef.current.value = "";
    }
  };

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      const droppedFile = e.dataTransfer.files[0];
      if (droppedFile) {
        onFileSelect(droppedFile);
      }
    },
    [onFileSelect],
  );

  const dropZone = (
    <label
      className={uploadZoneClasses(isDragging)}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <img src={papersIcon} alt="Upload documents" className="mb-3 h-14 w-14" />
      <p className="font-medium">Click to upload or drag and drop</p>
      <p className="text-tertiary">{acceptLabel}</p>
      <input
        ref={inputRef}
        type="file"
        className="hidden"
        accept={accept}
        onChange={handleFileChange}
      />
    </label>
  );

  return (
    <div className="flex flex-col gap-2">
      {label && (
        <p className="flex items-center gap-1.5 text-sm font-medium">
          {label}
          {labelSuffix && <span className="text-tertiary"> {labelSuffix}</span>}
          {labelTooltip && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Info className="h-3.5 w-3.5 shrink-0 cursor-pointer text-tertiary" />
                </TooltipTrigger>
                <TooltipContent>{labelTooltip}</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </p>
      )}
      {file ? (
        <FileCard
          fileName={file.name}
          fileSize={file.size}
          onRemove={handleRemoveFile}
          status={fileCardStatus}
          progress={fileCardProgress}
          previewUrl={fileCardPreviewUrl}
          errorMessage={fileCardErrorMessage}
        />
      ) : existingFileName && !existingRemoved ? (
        <FileCard fileName={existingFileName} onRemove={handleRemoveFile} />
      ) : (
        dropZone
      )}
    </div>
  );
};
